import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import useWindowWidth from "../../utils/windowWidth";
import dashboard from '../../assets/dashboard-img.svg';

const dashboardSections = [
  {
    title: "Document Accessibility",
    route: "/docaccess/new",
    description: "Convert PDF files into accessible DOCX files.",
    eyebrow: "File conversion",
    details: [
      "PDF up to 300 MB.",
      "Supports math, tables, lists, and headings.",
      "Use tags to organize files.",
      "You get an email and a portal download link.",
    ],
    action: "Open Document Accessibility",
  },
  {
    title: "Career Discovery",
    route: "/opportunity",
    description: "Get career guidance from disability-led lived experiences.",
    eyebrow: "Career guidance",
    details: ["Explore paths, choices, and work insights."],
    action: "Open Career Discovery",
  },
  {
    title: "Benefits",
    route: "/benefits",
    description: "Check benefits and support options in one place.",
    eyebrow: "Benefits screening",
    details: ["Review disability support options with less navigation."],
    action: "Open Benefits",
  },
  {
    title: "I-Tutor",
    route: "/itutor",
    description: "Learn with a voice-first AI tutor.",
    eyebrow: "Accessible learning",
    details: ["Clear lessons. Structured teaching. Interactive support."],
    action: "Open I-Tutor",
  },
];

function DashBoard() {
  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  useEffect(() => {
    document.title = "Home - Nclude Portal";
  }, []);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <div className="containerr">
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="DashBoard" />
        <div className="doc-container" role="main" tabIndex={-1} id="main-content" aria-live="polite">
          <section className="landing-page dashboard-page" aria-labelledby="dashboard-heading">
            <div className="dashboard-hero dashboard-container">
              <div className="dashboard-hero-copy">
                <h1 className="display-title dashboard-title" id="dashboard-heading" ref={mainRef}>
                  Welcome to NClude.AI
                </h1>
                {/* left aligned text with tailwind css */}
                <p className="display-12 dashboard-summary text-start" id="dashboard-summary">
                  Explore our services to enhance your experience and access the resources you need. From document accessibility to career guidance, benefits screening, and interactive learning with I-Tutor, we are here to support you every step of the way.
                </p>
              </div>
            </div>

            <section
              className="dashboard-grid-section"
              aria-labelledby="dashboard-services-heading"
              aria-describedby="dashboard-summary"
            >
              <div className="dashboard-section-heading">
                <h2 id="dashboard-services-heading">Available services</h2>
                <p>Open a page from the cards below.</p>
              </div>

              <div className="dashboard-grid" role="list" aria-label="NClude services">
                {dashboardSections.map((section) => {
                  const cardTitleId = `${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-title`;
                  const cardDescriptionId = `${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-description`;

                  return (
                    <article
                      key={section.title}
                      className="dashboard-tile"
                      role="listitem"
                      aria-labelledby={cardTitleId}
                      aria-describedby={cardDescriptionId}
                    >
                      <div className="dashboard-tile-content">
                        <p className="dashboard-tile-eyebrow">{section.eyebrow}</p>
                        <h3 id={cardTitleId} className="dashboard-tile-title">
                          {section.title}
                        </h3>
                        <p id={cardDescriptionId} className="dashboard-tile-description">
                          {section.description}
                        </p>
                        <ul className="dashboard-detail-list">
                          {section.details.map((detail) => (
                            <li key={detail}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                      <Link
                        to={section.route}
                        className="dashboard-tile-link"
                        aria-label={`${section.action}. Opens the ${section.title} page.`}
                      >
                        {section.action}
                      </Link>
                    </article>
                  );
                })}
              </div>
            </section>
          </section>
        </div>
      </div>
    </div>
  );
}

export default DashBoard;
